/**
 * ReviewListPage
 * 
 * Lista de requisições aguardando revisão técnica.
 * Exibe cards em grid com informações resumidas.
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search,
  User, Clock, ChevronRight, LayoutList,
  AlertCircle
} from 'lucide-react';
import { useReviewList } from '../../../hooks/usePurchases';
import './ReviewListPage.css';

const ReviewListPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  
  // Buscar lista de revisão
  const { requisitions, loading, error } = useReviewList();

  const getPriorityConfig = (priority) => {
    const configs = {
      critical: { label: 'Crítico', className: 'priority-critical' },
      urgent: { label: 'Urgente', className: 'priority-urgent' },
      normal: { label: 'Normal', className: 'priority-normal' }
    };
    return configs[priority] || configs.normal;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const filteredQueue = useMemo(() => {
    if (!requisitions) return [];
    
    return requisitions.filter(req => {
      const searchLower = searchTerm.toLowerCase();
      return (
        req.title?.toLowerCase().includes(searchLower) ||
        req.id?.toString().includes(searchTerm) ||
        req.requester_name?.toLowerCase().includes(searchLower)
      );
    });
  }, [requisitions, searchTerm]);

  return (
    <div className="review-list-page">
      {/* Header */}
      <div className="finance-list-header">
        <div>
          <h1 className="finance-list-title">
            Fila de <span className="finance-list-title-highlight">Revisão Técnica</span>
          </h1>
          <p className="finance-list-subtitle">
            Gerencie as pendências de auditoria de suprimentos
          </p>
        </div>
        <button 
          className="finance-list-back-button"
          onClick={() => navigate('/erp/purchases')}
        >
          <ArrowLeft size={16} />
          Painel de Compras
        </button>
      </div>

      {/* Toolbar */}
      <div className="finance-list-toolbar">
        <div className="finance-list-search">
          <Search className="finance-list-search-icon" size={18} />
          <input
            type="text"
            placeholder="Buscar por ID, título ou solicitante..."
            className="finance-list-search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="finance-list-loading">
          <div className="finance-list-loading-spinner"></div>
          <p>Carregando requisições para revisão técnica...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="finance-list-error">
          <AlertCircle size={24} />
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="finance-list-retry-button"
          >
            Tentar Novamente
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredQueue.length === 0 && (
        <div className="finance-list-empty">
          <LayoutList size={48} />
          <p>Nenhuma requisição encontrada</p>
          <span>
            {searchTerm 
              ? 'Tente ajustar os termos de busca'
              : 'Não há requisições aguardando revisão técnica'}
          </span>
        </div>
      )}

      {/* Grid of Cards */}
      {!loading && !error && filteredQueue.length > 0 && (
        <div className="finance-list-grid">
          {filteredQueue.map((req) => {
            const priorityConfig = getPriorityConfig(req.priority || 'normal');
            const itemsCount = req.items_count || req.items?.length || 0;
            
            return (
              <button
                key={req.id}
                className="finance-list-card"
                onClick={() => navigate(`/erp/purchases/review/${req.id}`)}
              >
                {/* Background Glow based on priority */}
                <div className={`finance-card-glow ${priorityConfig.className}`} />

                <div className="finance-card-header">
                  <div className="finance-card-badges">
                    <span className={`finance-priority-badge ${priorityConfig.className}`}>
                      {priorityConfig.label}
                    </span>
                  </div>
                  <span className="finance-card-id">#{req.id}</span>
                </div>
                
                <h3 className="finance-card-title">{req.title || 'Sem título'}</h3>
                
                <div className="finance-card-meta">
                  <div className="finance-card-meta-item">
                    <User size={12} />
                    <span>{req.requester_name || 'N/A'}</span>
                  </div>
                  <div className="finance-card-meta-item">
                    <Clock size={12} />
                    <span>{formatDate(req.created_at)}</span>
                  </div>
                </div>

                <div className="finance-card-footer">
                  <div className="finance-card-value">
                    <p className="finance-card-value-label">Itens</p>
                    <p className="finance-card-value-amount">{itemsCount}</p>
                  </div>
                  <div className="finance-card-arrow">
                    <ChevronRight size={18} />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReviewListPage;
